import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  CircularProgress,
  SxProps,
  Tooltip,
} from '@mui/material';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import UserAnalyticsTable from './UserAnalyticsTable';
import { analyticsOuterBoxStyles } from './DistributionAnalyticsTable';
import ViewRecipientsDialog, { loaderSx } from './ViewRecipientsDialog';
import { useParams } from 'react-router-dom';
import { getMandatoryMessages } from '../api/requests/users';
import { GridColDef } from '@mui/x-data-grid';
import { formatFullDateTime } from '../utils/utils';
import { sectionDescriptionSx, sectionTitleSx } from './UserAnalytics';
import { PaginationType } from './Groups';
import { textSx } from './MandatoryReplies';
import { viewReciepientStyles } from './MessageStatsTable';
import ActiveTickIcon from '../../src/assets/ActiveTickIcon.svg';

interface MandatoryMessageType {
  createdOn: string;
  dueTime: string;
  mandatoryStatus: string;
  messageId: number;
  messageText: string;
  recipientsCount: number;
  viewedCount: number;
}

interface RowsType {
  id: number;
  title: string;
  created: string;
  due: string;
  overdue: string;
  viewed: { done: number; total: number };
}

const renderProgress = (value: { done: number; total: number }, Icon: any) => {
  const percentage = Math.round((value.done / value.total) * 100);
  return (
    <Box sx={renderProgressWrapperSx}>
      <Box sx={renderProgressIconTextSx}>
        <Icon sx={{ fontSize: 16, color: '#2f2f2f' }} />
        <LinearProgress
          variant='determinate'
          value={percentage}
          sx={linearProgressSx}
        />
        <Typography fontSize='14px'>
          {value.done}/{value.total}
        </Typography>
      </Box>
    </Box>
  );
};

const MandatoryMessagesTable = () => {
  const { userId } = useParams();
  const [rows, setRows] = useState<RowsType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState<{
    id: number;
  } | null>(null);
  const [paginationModel, setPaginationModel] = useState<PaginationType>({
    page: 0,
    pageSize: 5,
  });
  const [totalRowsCount, setTotalRowsCount] = useState<number>(0);

  const viewDetailsHandler = (id: number) => {
    setSelectedRow({ id });
  };

  const closeDialog = () => {
    setSelectedRow(null);
  };

  const columns: GridColDef[] = [
    {
      field: 'title',
      headerName: 'Title',
      flex: 1.3,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <Typography fontSize='14px' sx={textSx}>
            {params.value || '--'}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'created',
      headerName: 'Created',
      flex: 1,
      renderCell: (params) => (
        <Typography fontSize='14px'>{params.value}</Typography>
      ),
    },
    {
      field: 'due',
      headerName: 'Due',
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ dispaly: 'flex', flexDirection: 'column' }}>
          <Typography fontSize='14px'>{params.row.due}</Typography>
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      renderCell: (params) => (
        <Box>
          <Box sx={overdueChipSx(params.row.overdue)}>
            <img src={ActiveTickIcon} style={{ marginRight: '10px' }} />
            {params.row.overdue === 'Overdue' ? params.row.overdue : 'Active'}
          </Box>
        </Box>
      ),
    },
    {
      field: 'viewed',
      headerName: 'Viewed',
      flex: 1,
      renderCell: (params) =>
        renderProgress(params.value, RemoveRedEyeOutlinedIcon),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      disableColumnMenu: true,
      width: 180,
      renderCell: (params) => (
        <Typography
          sx={viewReciepientStyles}
          onClick={() => viewDetailsHandler(params.row.id)}
        >
          View Recipients
        </Typography>
      ),
    },
  ];
  const fetchMandatoryMessages = async () => {
    setLoading(true);
    try {
      const response =
        userId &&
        (await getMandatoryMessages({
          userId,
          page: paginationModel.page,
          size: paginationModel.pageSize,
        }));
      if (response && response.userMandatoryMessages) {
        const updatedResponse = response.userMandatoryMessages.map(
          (item: MandatoryMessageType) => ({
            id: item.messageId,
            title: item.messageText,
            created: formatFullDateTime(item.createdOn),
            due: formatFullDateTime(item.dueTime),
            overdue: item.mandatoryStatus,
            viewed: { done: item.viewedCount, total: item.recipientsCount },
          })
        );
        setRows(updatedResponse);
        setTotalRowsCount(response.totalCount);
      } else {
        setRows([]);
        setTotalRowsCount(0);
      }
    } catch (error) {
      console.error('Failed to fetch mandatory messages', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMandatoryMessages();
  }, [paginationModel]);

  return (
    <Box sx={analyticsOuterBoxStyles}>
      <Typography sx={sectionTitleSx}>Mandatory Messages</Typography>
      <Typography sx={sectionDescriptionSx}>
        Messages that require acknowledgment or response
      </Typography>
      {loading ? (
        <Box sx={loaderSx}>
          <CircularProgress size={60} thickness={6} />
        </Box>
      ) : rows.length > 0 ? (
        <UserAnalyticsTable
          columns={columns}
          rows={rows}
          sx={{ height: '100%', width: '100%' }}
          setPaginationModel={setPaginationModel}
          paginationModel={paginationModel}
          totalRowsCount={totalRowsCount}
        />
      ) : (
        <Box sx={loaderSx}>
          <Typography variant='body2' color='textSecondary'>
            No data found.
          </Typography>
        </Box>
      )}
      {selectedRow && (
        <ViewRecipientsDialog
          open={!!selectedRow}
          handleClose={closeDialog}
          id={selectedRow.id}
          title='Mandatory Message Recipients'
          description='List of all recipients and their interaction status'
        />
      )}
    </Box>
  );
};

export default MandatoryMessagesTable;

const overdueChipSx: (value: string) => SxProps = (value) => ({
  height: '18px',
  display: 'flex',
  alignItems: 'center',
  paddingLeft: 1,
  fontSize: '14px',
  borderRadius: '16px',
  width: '100%',
});

const renderProgressIconTextSx: SxProps = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
};

const renderProgressWrapperSx: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'start',
  width: '80%',
  gap: 0.5,
};

const linearProgressSx: SxProps = {
  width: '75%',
  height: 8,
  borderRadius: 4,
  backgroundColor: '#e0e0e0',
  '& .MuiLinearProgress-bar': {
    backgroundColor: '#0b1a30',
  },
};
